import { compose, mdsWidget } from 'dojo/_base/declare';
import template from './EvProcessEditor.html?raw';

import _StatefulSection from '../../moreCore/UI/_StatefulSection/_StatefulSection';
import Edit from '../../moreCore/UI/Edit/Edit';
import SearchSelect from "../../moreCore/UI/SearchSelect/SearchSelect";
import _uiFrameMixin from '../../moreCore/UI/Container/uiFrame';
import HintBox from '../../moreCore/UI/HintBox/HintBox';
import lang from 'dojo/_base/lang';
import _GlobalSelectEntityDialogsMixin from '../_loadingHelpers/_GlobalSelectEntityDialogsMixin';
import DateTimeInput from '../../moreCore/UI/DateTimeInput/DateTimeInput';
import StaticTable from '../../moreCore/UI/StaticTable/StaticTable';
import EntityStore from '../../moreCore/Tools/EntityStore';
import ExpandableSection from '../../moreCore/UI/Container/ExpandableSection';
import EvProcessRelatedAddressEditor from './EvProcessRelatedAddressEditor';
import { ExpandableSectionMarkings } from "../../moreCore/UI/Container/ExpandableSectionTypes";
import { EvProcess, EvProcessAddress, EvProcessGroup, EvProcessRelatedAddress, EvProcessSEDModes, ProcessTypes, RefBookingEvProcess, RefProjectEvProcess, RefRoomEvProcess } from "./EvProcessTypes";
import { SelectEntityDialogTypes, SelectEntityDialog } from "../../moreCore/_loadingHelpers/SelectEntityDialog";

class EvProcessEditor extends compose(_StatefulSection, _uiFrameMixin, _GlobalSelectEntityDialogsMixin) {
    templateString: any = template;

    private contextEntity: EvProcessGroup | RefProjectEvProcess | RefBookingEvProcess;

    inputTemplateSection: HTMLDivElement;
    inputProjectSection: HTMLDivElement;
    inputBookingSection: HTMLDivElement;
    inputRoomOfBookingSection: HTMLDivElement;
    inputServiceBookingSection: HTMLDivElement;
    inputRoomSection: HTMLDivElement;

    inputServiceBooking: MdsLax<InstanceType<typeof HintBox>>;
    inputProcessStatus: MdsLax<InstanceType<typeof HintBox>>;
    inputRoom: MdsLax<InstanceType<typeof HintBox>>;
    inputProject: MdsLax<InstanceType<typeof Edit>>;
    inputBooking: MdsLax<InstanceType<typeof Edit>>;
    inputRoomOfBooking: MdsLax<InstanceType<typeof Edit>>;

    inputDateStart: MdsLax<InstanceType<typeof DateTimeInput>>;
    inputDateEnd: MdsLax<InstanceType<typeof DateTimeInput>>;

    relatedAddressExpander: MdsLax<InstanceType<typeof ExpandableSection>>;
    relatedAddressButton: HTMLButtonElement;
    tableContainer: HTMLDivElement;
    relatedAddressTable: MdsLax<InstanceType<typeof StaticTable>>;
    relatedAddressStore: MdsLax<InstanceType<typeof EntityStore<EvProcessRelatedAddress>>>;

    relationGroupsList: Array<any>;
    selectRegistry: Array<MdsLax<InstanceType<typeof SearchSelect>>> = [];


    private type: ProcessTypes;

    constructor(type: ProcessTypes) {
        super(...(arguments as unknown as []));
        this.type = type;
    }

    private _tableRowConfig: StaticTable.RowConfig = {
        attributes: ["order", "c_address_1", "ref_relation_group.description"],
        colNamesInternal: ["order", "c_address_1", "ref_relation_group"],
        headings: [
            (this as any).translate("General_Label_Order"),
            (this as any).translate("General_Label_Address"),
            (this as any).translate("General_Label_Group")
        ],
        colspans: [1, 2, 2]
    }

    private _tableLayout: StaticTable.Layout = {
        label: "relatedAddressTable",
        name: "relatedAddressTable",
        rows: [(this as any)._tableRowConfig]
    };

    private _tableFormatter(cell: StaticTable.StaticTableCell<EvProcessRelatedAddress>) {
        let node = cell.contentnode;
        let entity = cell.entity;
        let colName = cell.name;
        let value = cell.value;
        let index = cell.tableRow.rowGroup.rowIndex;

        switch (colName) {
            case "c_address_1":
                let cellContent = entity.c_address_1 ? entity.c_address_1 : entity.ref_address ? entity.ref_address.gDisp_Firstname_Name : entity.$Displayname;
                return true;
        }
    }

    private _onTableRowClick(e: StaticTable.StaticTableRow<EvProcessRelatedAddress>): void {
        let addressEditor = new EvProcessRelatedAddressEditor(this.type);
        var entity = e.rowGroup.entity;
        addressEditor.setup(entity);
        addressEditor.on("confirmed", lang.hitch(this, this._onEvProcessRelatedAddressConfirmed));
    }

    _onEvProcessRelatedAddressConfirmed(data: EvProcessRelatedAddress): void {
        this.relatedAddressStore.changeEntry(data, true);
    }

    private _setupTable(): void {
        this.relatedAddressTable = new StaticTable;
        this.relatedAddressTable.setLayout(this._tableLayout);
        this.relatedAddressTable.addDefaultRowActionButton("delete");
        this.relatedAddressTable.addDefaultRowActionButton("rowsorting");
        this.relatedAddressTable.setRowClick(lang.hitch(this, this._onTableRowClick));

        this.relatedAddressTable.setupFromStore(this.relatedAddressStore);


        this.relatedAddressTable.placeAt(this.tableContainer);

        this.relatedAddressTable.setCyAttribute("evProcessEditorRelatedAddressTable");

        this._setExpanderMarking();
    }

    private _setExpanderMarking() {
        let completeList = this.relatedAddressStore.getCurrentCompleteList();
        this.relatedAddressExpander.setMarking(completeList?.length ? ExpandableSectionMarkings.hasContent : ExpandableSectionMarkings.default);
    }

    protected afterSetData(): void {
        super.afterSetData.apply(this, arguments as any);

        let booking = this.getAttributeValue("ref_ev_booking");
        if (!booking) {
            this.hideNode(this.inputRoomOfBookingSection, true);
            this.hideNode(this.inputRoom, false);
            this.hideNode(this.inputRoomSection, false);
        }

        var relatedAddresses = this.getAttributeValue("EL_ht_address_ev_process_ev_process");
        let addressDiffData = this.getAttributeValue("addressDiffData");

        this.relatedAddressStore.setup(relatedAddresses);
        this.relatedAddressStore.setDiffData(addressDiffData);

        this._setExpanderMarking();
    }

    private handleVisibility(): void {
        switch (this.type) {
            case "template":
                this.hideNode(this.inputTemplateSection, false);
                this.hideNode(this.inputRoomSection, false);
                break;
            case "project":
                this.hideNode(this.inputProjectSection, false);
                this.hideNode(this.inputBookingSection, false);
                this.hideNode(this.inputRoomOfBookingSection, false);
                this.hideNode(this.inputServiceBookingSection, false);
                break;
            case "booking":
                this.hideNode(this.inputServiceBookingSection, false);
                this.hideNode(this.inputProjectSection, false);
                this.hideNode(this.inputBookingSection, false);
                this.hideNode(this.inputRoomOfBookingSection, false);
                break;
        }
    }

    private _onClickConfirm(): void {
        this.emit("confirmed", this.getCurrentData());
    }

    private _onChangeIsFullTime(e: Event) {
        if (e && this.inputDateStart.getValue().date) {
            var newDateEnd = new Date(this.inputDateStart.getValue().date.getTime() + 1439 * 60000);
            this.inputDateEnd.setValue(newDateEnd);
        }
    }

    private _onChangeDateStart(e: Event) {
        if (this.inputDateStart.getValue().date) {
            var timeDifference = this.getAttributeValue("duration") * 60000;
            var newDateEnd = new Date(this.inputDateStart.getValue().date.getTime() + timeDifference);

            let dateStart = this.inputDateStart.getValue();
            this.setAttributeValue("date_start_offset", -dateStart.date.getTimezoneOffset());

            this.setAttributeValue("date_end", newDateEnd);
        }
    }

    private _onChangeDateEnd(e: Event) {
        if (this.inputDateEnd.getValue().date) {
            if (this.inputDateStart.getValue().date) {
                var newDuration = this.inputDateEnd.getValue().date.getTime() - this.inputDateStart.getValue().date.getTime();
                newDuration = newDuration / 60000;
                this.setAttributeValue("duration", newDuration);
            }

            let dateEnd = this.inputDateEnd.getValue();
            this.setAttributeValue("date_end_offset", -dateEnd.date.getTimezoneOffset());
        } else {
            this.setAttributeValue("duration", null);
        }
    }

    private _onChangeStore(e: Event): void {
        let storeDiffData = this.relatedAddressStore.getDiffData();
        this.setAttributeValue("addressDiffData", storeDiffData);

        let storeCompleteList = this.relatedAddressStore.getCurrentCompleteList();
        this.setAttributeValue("EL_ht_address_ev_process_ev_process", storeCompleteList);

        this._setExpanderMarking();
    }

    setupForm(data?: EvProcess) {
        if (data) {
            this.setData(data);
        }

        console.log(data);

        var heading = data ? data.ev_process_name ? data.ev_process_name : this.translate("DLG_Heading_NoHeading") : `${this.translate("General_New")}: ${this.translate("General_Label_EvProcess")}`;

        this.show("dlg", heading, null);
        this.createDefaultButton("ok", lang.hitch(this, this._onClickConfirm));
        this.relatedAddressButton.addEventListener("click", lang.hitch(this, this._onClickRelatedAddressAdd));

        if (!data) {
            this.setStartupState("ready");
        }
    }

    setTemplate(template: EvProcessGroup) {
        this.setAttributeValue("ref_event_process_group", template);
    }

    setProject(project: RefProjectEvProcess) {
        this.setAttributeValue("ref_event_process_group", project);
        this.setAttributeValue("ref_ev_project", project);

        this.inputProject.setValue(project.gDisp_1, true);

        this.hideNode(this.inputRoomOfBookingSection, true);
        this.hideNode(this.inputRoom, false);
        this.hideNode(this.inputRoomSection, false);
    }

    setBooking(booking) {
        this.setAttributeValue("ref_event_process_group", booking);
        this.setAttributeValue("ref_ev_booking", booking);
        this.setAttributeValue("ref_ev_project", booking.ref_ev_project);

        this.inputProject.setValue(booking.ref_ev_project.gDisp_1, true);
        this.inputBooking.setValue(booking.gDisp_1, true);
        this.inputRoomOfBooking.setValue(booking.ref_ev_room.description, true)



    }

    private _hasStoreAddress(address: EvProcessAddress): boolean {
        let completeList = this.relatedAddressStore.getCurrentCompleteList();
        for (let relatedAddress of completeList) {
            if (relatedAddress.ref_address.ID === address.ID) return true;
        }
        return false;
    }

    private _onClick_OpenRoomsSED(e) {
        this.loadSelectEntityDialog(e, "selectEvRooms", lang.hitch(this, this._onCallback_Dlg));
    }

    private _onClickRelatedAddressAdd(e): void {
        let mode = EvProcessSEDModes[this.type];



        this.loadSelectEntityDialog(e, mode, lang.hitch(this, this._onCallback_Dlg), null, { projectEntity: this.getAttributeValue("ref_ev_project"), bookingEntity: this.getAttributeValue("ref_ev_booking") });
    }

    setContextEntity(entity: EvProcessGroup | RefProjectEvProcess | RefBookingEvProcess) {
        this.contextEntity = entity;
    }

    private _onCallback_Dlg(mode: string, data, items: Array<SelectEntityDialogTypes.Item<EvProcessAddress | RefRoomEvProcess>>) {
        switch (mode) {
            case "selectTemplateAddresses":
            case "selectProjectAddresses":
            case "selectBookingAddresses":
                if (items) {
                    for (let item of items) {
                        let relatedAddressCompleteList = this.relatedAddressStore.getCurrentCompleteList();
                        let lastRelatedAddress = relatedAddressCompleteList[relatedAddressCompleteList.length - 1];
                        let order = lastRelatedAddress ? lastRelatedAddress.order + 1 : 1;

                        let addressEntity = item.entry.data as EvProcessAddress;
                        let newRelatedAddressEntity: EvProcessRelatedAddress = {
                            $Displayname: addressEntity.$Displayname,
                            Classname: "ht_address_ev_process",
                            ClassPrintname: "beteiligte Adresse (VA_Ablauf)",
                            c_address_1: addressEntity.gDisp_Firstname_Name,
                            ID: Math.random() * 1000000000000 + "",
                            note: "",
                            note_internal_use: "",
                            order: order,
                            ref_address: addressEntity,
                            ref_relation_group: null,
                        }
                        if (!this._hasStoreAddress(addressEntity)) this.relatedAddressStore.addEntry(newRelatedAddressEntity);
                        else this.showToast(this.translate("EvProcessEditor_Error_AddressInStore") + ": " + addressEntity.$Displayname, "fas fa-exclamation-triangle");
                    }
                }
                break;
            case "selectEvRooms":
                if (items) {
                    let room = items[0].entry.data as RefRoomEvProcess;
                    this.inputRoom.setValue(room);
                }
                break;
            default:
                console.error("Error: invalid sed mode");
                break;
        }
    }

    postCreate(): void {
        super.postCreate.apply(this, arguments as any);

        this.handleVisibility();

        this.relatedAddressStore = new EntityStore();
        this.relatedAddressStore.on("change", lang.hitch(this, this._onChangeStore));
        this._setupTable();

        this.SEDconfigCallfunction = "Webtool_API__EvProcess_GetSEDConfig";

        this.inputRoom.addInlineButton(lang.hitch(this, this._onClick_OpenRoomsSED));
    }

    startup(): void {
        super.startup.apply(this, arguments as any);

    }
}

export default mdsWidget(EvProcessEditor);
